import React from 'react'
import { useTabNavigation } from 'hooks/useTabNavigation'

/**
 * TabContentRenderer
 *
 * "Stable Element" Pattern:
 *
 *   • To completely stop refetching, we don't just hide the component; 
 *     we freeze the actual React Element reference for each tab.
 *
 *   • When switching tabs, we pull the EXACT same element from the cache.
 *     Because the reference is identical, React skips the reconciliation
 *     process entirely for that component, guaranteeing that its state 
 *     is preserved and its useEffect (data fetching) NEVER fires again.
 *
 *   • Component and its initial props are "locked" the first time the tab opens.
 */
export const TabContentRenderer = ({ tabs, activeTabId, Component, routeProps, layoutConfig }) => {
    const { tabCache } = useTabNavigation()

    const hasActiveTab = !!activeTabId && tabs.some(tab => tab.id === activeTabId)
    const isRootOrDashboard = !hasActiveTab

    return (
        <div className="tab-navigation-container" style={{ position: 'relative', height: '100%' }}>
            {/* 1. ROOT/DASHBOARD SLOT: 
                 Used for paths that are NOT converted into tabs (like landing pages or errors).
                 We hide it when a tab is active. */}
            <div 
                key="root-content-slot"
                style={{ display: isRootOrDashboard ? 'block' : 'none' }}
                className="tab-content-wrapper native-scroll"
            >
                {/* We only render the "Live" component if we are NOT on a tab path 
                    to avoid dual-mounting. If the current URL has a tab, we render 
                    the tab's slot instead. */}
                {isRootOrDashboard && Component && <Component {...routeProps} />}
            </div>

            {/* 2. TAB SLOTS: 
                 Every tab gets its own persistent DOM element. */}
            {tabs.map(tab => {
                const isVisible = tab.id === activeTabId
                const cached = tabCache[tab.id]

                // IMPORTANT: We render the div even if cache is empty to maintain DOM order
                return (
                    <div
                        key={tab.id}
                        style={{ display: isVisible ? 'block' : 'none' }}
                        className="tab-content-wrapper native-scroll"
                        aria-hidden={!isVisible}
                    >
                        {cached?.element || (isVisible && Component && <Component {...routeProps} />)}
                    </div>
                )
            })}
        </div>
    )
}
